import React, { Fragment, useMemo } from "react";
import { ChartBreakoutSquare } from "../../icons/ChartBreakoutSquare";
import { CheckCircle } from "../../icons/CheckCircle";
import { LifeBuoy02 } from "../../icons/LifeBuoy02";
import { Settings01 } from "../../icons/Settings01";
import { Settings04 } from "../../icons/Settings04";
import { LogOut } from "../../icons/LogOut";
import { Zap } from "../../icons/Zap";
import { classNames } from "@/lib/utils";
import { PieChart022 } from "../../icons/PieChart022";
import { Star052 } from "../../icons/Star052";
import { Dialog, Menu, Transition } from '@headlessui/react'
import { Cog6ToothIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { Table } from "@/types";
import { useModalContext } from "@/contexts/ModalContextProvider";
import ImageWithFallback from "@/components/presentational/tailwind/ImageWithFallback";
import { ROOT_DOMAIN } from "@/constants";
import Anchor from "@/components/Anchor/Anchor";
import { usePathname } from "next/navigation";
import { match } from "path-to-regexp";
import { Logo } from "src/components/landing/navigation/Logo";

interface Props {
  className?: any;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  accounts: Table<'accounts'>[];
}

export const Sidebar = ({ className, sidebarOpen, setSidebarOpen, accounts }: Props & { sidebarOpen: boolean, setSidebarOpen: (open: boolean) => void, accounts: any[] }): JSX.Element => {

  const { setIsConnectAccountModalOpened, setIsUpgradeModalOpened } = useModalContext();

  const pathName = usePathname() || '';
  const matchSettingsPath = match("/app/settings(.*)?");
  const isSettingsPath = Boolean(matchSettingsPath(pathName));

  // Extract the account ID from the URL path (/app/account/:accountId or /app/account/:accountId/settings, etc.)
  const accountId = pathName.split("/")[3];
  console.log("🚀 ~ file: Sidebar.tsx ~ accountId", accountId)

  // const searchParams = useSearchParams();// query parameters => if ever needed
  const tabs = useMemo(() => {
    const basePath = '/app';
    const isPathActive = (expectedPath: string): boolean => {
      const normalizedPath = pathName.endsWith('/') ? pathName.slice(0, -1) : pathName;
      const normalizedExpectedPath = `${basePath}/account/${accountId}${expectedPath}`.endsWith('/') ? `${basePath}/account/${accountId}${expectedPath}`.slice(0, -1) : `${basePath}/account/${accountId}${expectedPath}`;
      return normalizedPath === normalizedExpectedPath;
    };

    return [
      { name: 'Dashboard', href: accountId ? `${basePath}/account/${accountId}` : `${basePath}`, icon: ChartBreakoutSquare, isActive: isPathActive('') },
      { name: 'Growth Settings', href: accountId ? `${basePath}/account/${accountId}/settings` : `${basePath}`, icon: Settings04, isActive: isPathActive('/settings') },
      { name: 'Analytics', href: accountId ? `${basePath}/account/${accountId}/analytics` : `${basePath}`, icon: PieChart022, isActive: isPathActive('/analytics') },
      // { name: 'AI Tools', href: accountId ? `${basePath}/account/${accountId}/ai-training` : `${basePath}`, icon: Zap, isActive: isPathActive('/ai-training') },
      { name: 'AI Tools', href: 'https://www.keesy.com/free-instagram-tools', icon: Zap, isActive: isPathActive('/ai-training') },
      { name: 'Keesy AI', href: accountId ? `${basePath}/account/${accountId}/keesy-ai` : `${basePath}`, icon: Star052, isActive: false }, // Assuming this tab doesn't have a corresponding path
    ];
  }, [accountId, pathName]);

  const generateNewUrlWithAccountId = (newAccountId: string) => {
    if (pathName.startsWith('/app')) {
      return `/app/account/${newAccountId}`;
    }
    return pathName.replace(/\/account\/[^/]+/, `/account/${newAccountId}`);
  };

  return (
    <>
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex flex-1 w-full max-w-xs mr-16">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 flex justify-center w-16 pt-5 left-full">
                    <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="w-6 h-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>
                {/* Sidebar component, swap this element with another sidebar if you like */}
                <div className="flex flex-col px-6 pb-4 overflow-y-auto bg-white grow gap-y-5">
                  <div className="absolute w-[433px] h-[1116px] top-[-216px] left-[-161px]">
                    <div className="w-[324px] h-[324px] top-0 left-0 rounded-[162px] [background:linear-gradient(180deg,rgb(117,73,244)_0%,rgb(252,166,233)_100%)] saturate-150 absolute blur-[90px] opacity-[0.32]" />
                  </div>

                  <div className="z-10 flex items-center h-16 shrink-0">
                    <Logo />
                  </div>
                  <nav className="z-10 flex flex-col flex-1">
                    <ul role="list" className="flex flex-col flex-1 gap-y-6">
                      <li>
                        <TabsList tabs={tabs} />
                      </li>
                      <li>
                        <AccountList accounts={accounts} accountId={accountId} generateNewUrlWithAccountId={generateNewUrlWithAccountId} setIsConnectAccountModalOpened={setIsConnectAccountModalOpened} />
                      </li>
                      <li className="mt-auto">
                        <SidebarFooter isSettingsPath={isSettingsPath} setIsConnectAccountModalOpened={setIsConnectAccountModalOpened} />
                      </li>
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      <div className="flex flex-col px-6 pb-4 overflow-y-auto grow">
        <Anchor href="/app" className="flex items-center h-20 shrink-0">
          <Logo />
        </Anchor>
        <nav className="flex flex-col flex-1">
          <ul role="list" className="flex flex-col flex-1 gap-y-6">
            <li>
              <TabsList tabs={tabs} />
            </li>

            <svg width="100%" height="1" viewBox="0 0 100% 1" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line y1="0.5" x2="100%" y2="0.5" stroke="#E1E2E4" strokeDasharray="3 3" />
            </svg>

            <li
            // FIXME: fix this
            // style={{ maxHeight: '450px' }} className="overflow-scroll overflow-x-visible scroll-gradient-shadow"
            >
              <AccountList accounts={accounts} accountId={accountId} generateNewUrlWithAccountId={generateNewUrlWithAccountId} setIsConnectAccountModalOpened={setIsConnectAccountModalOpened} />
            </li>
            <li className="mt-auto space-y-1">
              <SidebarFooter isSettingsPath={isSettingsPath} setIsConnectAccountModalOpened={setIsConnectAccountModalOpened} />
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
};

const TabsList = ({ tabs }) => {
  return (
    <ul role="list" className="space-y-1">
      {tabs.map((item) => (
        <li key={item.name}>
          <Anchor href={item.href}
            className={classNames(
              item.isActive
                ? 'bg-white'
                : 'text-[#16171C] hover:opacity-75',
              'group flex gap-x-3 rounded-lg px-3 py-2 text-sm leading-6 font-medium ease-in-out duration-200 transition-all'
            )}
            style={item.isActive ? { boxShadow: '0px 0px 0px 1px rgba(47, 10, 154, 0.08), 0px 1px 2px 0px rgba(47, 10, 154, 0.10)' } : {}}
          >
            <item.icon className='w-6 h-6 shrink-0' aria-hidden="true" />
            {item.name}
          </Anchor>
        </li>
      ))}
    </ul>
  )
}


const AccountList = ({ accounts, accountId, generateNewUrlWithAccountId, setIsConnectAccountModalOpened }) => {
  return (
    <>
      <div className="inline-flex px-3 text-sm font-semibold leading-6 text-gray-600">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" className='mr-1.5 h-6 w-6 translate-y-[-2px]'>
          <path d="M10 7.9C8.83983 7.9 7.9 8.84017 7.9 10C7.9 11.1601 8.84018 12.1 10 12.1C11.1601 12.1 12.1 11.1598 12.1 10C12.1 8.83989 11.1598 7.9 10 7.9ZM10 6.5C11.9323 6.5 13.5 8.06597 13.5 10C13.5 11.9324 11.934 13.5 10 13.5C8.06767 13.5 6.5 11.934 6.5 10C6.5 8.06767 8.06597 6.5 10 6.5ZM14.55 6.3244C14.55 6.80748 14.1575 7.19942 13.675 7.19942C13.1919 7.19942 12.8 6.80688 12.8 6.3244C12.8 5.84193 13.1925 5.45 13.675 5.45C14.1568 5.44939 14.55 5.84193 14.55 6.3244ZM10 4.4C8.26786 4.4 7.98554 4.40458 7.1799 4.44046C6.63102 4.46623 6.26309 4.54005 5.92123 4.67277C5.61743 4.79059 5.39828 4.93129 5.16478 5.16478C4.93038 5.39918 4.78994 5.61774 4.67259 5.92168C4.53957 6.26432 4.46577 6.63168 4.44046 7.1798C4.40424 7.95264 4.4 8.22273 4.4 10C4.4 11.7322 4.40459 12.0145 4.44045 12.82C4.46623 13.3687 4.54015 13.7372 4.67255 14.0782C4.79068 14.3824 4.93166 14.6021 5.16404 14.8345C5.39936 15.0695 5.61857 15.2104 5.91962 15.3266C6.26565 15.4604 6.63335 15.5343 7.1798 15.5595C7.95264 15.5957 8.22274 15.6 10 15.6C11.7322 15.6 12.0145 15.5954 12.82 15.5595C13.3675 15.5338 13.7363 15.4597 14.0782 15.3274C14.3816 15.2096 14.602 15.0681 14.8345 14.8359C15.0698 14.6003 15.2105 14.3816 15.3268 14.0798C15.4602 13.7351 15.5343 13.3669 15.5596 12.8202C15.5957 12.0474 15.6 11.7772 15.6 10C15.6 8.26786 15.5954 7.98555 15.5596 7.17996C15.5338 6.63234 15.4597 6.26304 15.3272 5.92123C15.2097 5.61827 15.0685 5.39844 14.8352 5.16478C14.6004 4.93 14.3824 4.78985 14.0783 4.67259C13.7359 4.53968 13.368 4.46577 12.8202 4.44047C12.0474 4.40423 11.7772 4.4 10 4.4ZM10 3C11.9016 3 12.1391 3.007 12.8858 3.042C13.6306 3.07642 14.1388 3.19425 14.585 3.3675C15.0464 3.54542 15.4361 3.78575 15.8251 4.17483C16.2136 4.56392 16.454 4.95475 16.6325 5.415C16.8051 5.86067 16.923 6.36933 16.958 7.11425C16.9913 7.86092 17 8.09833 17 10C17 11.9017 16.993 12.1391 16.958 12.8857C16.9236 13.6307 16.8051 14.1387 16.6325 14.585C16.4546 15.0464 16.2136 15.4361 15.8251 15.8252C15.4361 16.2137 15.0446 16.454 14.585 16.6325C14.1388 16.8052 13.6306 16.923 12.8858 16.958C12.1391 16.9913 11.9016 17 10 17C8.09833 17 7.86091 16.993 7.11425 16.958C6.36933 16.9236 5.86183 16.8052 5.415 16.6325C4.95416 16.4546 4.56391 16.2137 4.17483 15.8252C3.78575 15.4361 3.546 15.0447 3.3675 14.585C3.19425 14.1387 3.077 13.6307 3.042 12.8857C3.00875 12.1391 3 11.9017 3 10C3 8.09833 3.007 7.86092 3.042 7.11425C3.07641 6.36875 3.19425 5.86125 3.3675 5.415C3.54541 4.95417 3.78575 4.56392 4.17483 4.17483C4.56391 3.78575 4.95475 3.546 5.415 3.3675C5.86125 3.19425 6.36875 3.077 7.11425 3.042C7.86091 3.00875 8.09833 3 10 3Z" fill="url(#paint0_linear_2004_838)" />
        </svg>
        <span className="ml-1.5 text-transparent bg-gradient-to-r from-[#785bdf] to-[#ee84d7] bg-clip-text">
          Instagram Accounts
        </span>
      </div>
      {/* ADD:
                  max-h-[450px] overflow-scroll overflow-x-visible scroll-gradient-shadow
              */}
      <ul role="list" className="mt-3 space-y-2.5">
        {accounts.sort((a, b) => {
          // Selected account always first
          if (a.id === accountId) return -1;
          if (b.id === accountId) return 1;
          return 0;
        }).map((igAccount) => (
          <li key={igAccount.username}>
            <Anchor href={generateNewUrlWithAccountId(igAccount.id)}
              className={classNames(
                igAccount.id == accountId
                  ? 'bg-white text-[#16171C]'
                  : 'hover:opacity-80',
                'group flex justify-between gap-x-3 rounded-lg px-2.5 py-1 text-sm leading-6 font-semibold ease-in-out duration-200 cursor-pointer transition-all border-1 border-transparent hover:border-gray-100 w-full'
              )}
              style={igAccount.id == accountId ? { boxShadow: '0px 0px 0px 1px rgba(47, 10, 154, 0.08), 0px 1px 2px 0px rgba(47, 10, 154, 0.10)' } : {}}
            >
              <div className="flex items-center gap-x-3">

                <ImageWithFallback
                  unoptimized={true}
                  loading='eager'
                  width={60}
                  height={60}
                  className="flex items-center self-center justify-center rounded-full w-9 h-9 shrink-0"
                  src={igAccount.avatar_url || "/img/default_avatar.jpeg"}
                  alt={`${igAccount.username}'s profile image`}
                  placeholder="blur"
                  blurDataURL="/img/default_avatar.jpeg"
                  fallback={"/img/default_avatar.jpeg"}
                />
                <div className="flex flex-col leading-5">
                  <span className="font-semibold truncate max-w-[150px]">{igAccount.username}</span>
                  <span className="font-normal text-gray-600 truncate">{igAccount.followers.toLocaleString()} Followers</span>
                </div>
              </div>
              {(igAccount.id == accountId) && <CheckCircle className="self-center w-5 h-5 shrink-0" aria-hidden="true" />}
            </Anchor>
          </li>
        ))}

        <li className="flex justify-between p-3 !mt-0.5 transition-all duration-200 ease-in-out rounded-lg cursor-pointer opacity-75 group hover:opacity-100">
          <div className="flex items-center gap-x-3">
            <div className='flex items-center self-center justify-center bg-white rounded-full w-9 h-9 shrink-0' style={{ boxShadow: '0px 0px 0px 1px rgba(47, 10, 154, 0.08), 0px 1px 2px 0px rgba(47, 10, 154, 0.10)' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8.00004 3.33337V12.6667M3.33337 8.00004H12.6667" stroke="#16171C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className="text-sm font-medium text-gray-700 truncate"
              onClick={() => {
                // If is on demo subdomain, redirect to /sign-up
                if (window.location.hostname === "demo.keesy.com") {
                  window.location.href = `https://www.${ROOT_DOMAIN}/sign-up`;
                  return;
                }
                // Else show the Connect Account modal
                setIsConnectAccountModalOpened(true)
              }}
            >
              Connect Account
            </span>
          </div>
        </li>
      </ul>
    </>)
}

const SidebarFooter = ({ isSettingsPath, setIsConnectAccountModalOpened }: { isSettingsPath: boolean; setIsConnectAccountModalOpened: (opened: boolean) => void; }) => {
  return (
    <>
      <Anchor href="/app/settings" className={classNames(
        isSettingsPath
          ? 'bg-white'
          : 'text-gray-700 hover:opacity-80 hover:bg-white/50',
        'group flex gap-x-3 rounded-lg px-3 py-2 text-sm leading-6 font-medium ease-in-out duration-200 transition-all border-1 border-transparent hover:border-gray-100'
      )}
        style={isSettingsPath ? { boxShadow: '0px 0px 0px 1px rgba(47, 10, 154, 0.08), 0px 1px 2px 0px rgba(47, 10, 154, 0.10)' } : {}}>
        <Settings01 className="w-6 h-6 shrink-0" aria-hidden="true" />
        Settings
      </Anchor>
      <a href="javascript:Intercom('showNewMessage')" className={classNames('text-gray-700 hover:opacity-80 hover:bg-white/50',
        'group flex gap-x-3 rounded-lg px-3 py-2 text-sm leading-6 font-medium ease-in-out duration-200 transition-all border-1 border-transparent hover:border-gray-100'
      )}>
        <LifeBuoy02 className="w-6 h-6 shrink-0" aria-hidden="true" />
        Support
      </a>

      <a href="/api/logout" className={classNames('text-gray-700 hover:opacity-80 hover:bg-white/50',
        'group flex gap-x-3 rounded-lg px-3 py-2 text-sm leading-6 font-medium ease-in-out duration-200 transition-all border-1 border-transparent hover:border-gray-100'
      )}>
        <LogOut className="w-6 h-6 shrink-0" aria-hidden="true" />
        Log Out
      </a>

      <svg className="relative !my-5" width="100%" height="1" viewBox="0 0 100% 1" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line y1="0.5" x2="100%" y2="0.5" stroke="#E1E2E4" strokeDasharray="3 3" />
      </svg>

      <p className="text-xs font-medium leading-6 text-transparent bg-gradient-to-r from-[#9b89da] to-[#e894d6] bg-clip-text">
        © 2024 Keesy Inc. All rights reserved.
      </p>
    </>
  )
}